import { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import DongleIpDetectToolbar from "./DongleIpDetectToolbar";
import MainBtn from "../../main/main_btn/MainBtn";
import CreateHomeModal from "../../home/create_home_modal/CreateHomeModal";
import CreateDeviceModal from "../../home/create_device_modal/CreateDeviceModal";
import ConfigureWiFiModal from "../../device/ConfigureWiFiModal";
import "./style.scss";
import { DispatchProps, StateProps } from "../../../store";
import { mapDispatchToProps } from "../../../store/dispatcher";
import { connect } from "react-redux";
import { App, Button, Input, Popconfirm, Popover, Select, Switch, Tooltip } from "antd";
import {
  generate_for_device,
  generate_from_device,
} from "./DeviceSettings";
import { Home } from "../../../lib/Home";
import { countries } from "../../../constants/constants";
import { useTranslation } from "react-i18next";
import axios from "../../../services/axios";
import { InfoCircleOutlined, QuestionCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { Device } from "../../../lib/Device";
import { useNavigate } from "react-router-dom";
import { ReactComponent as NewHome } from "../../../assets/icons/new-home.svg";
import { ReactComponent as NewDevice } from "../../../assets/icons/new-device.svg";
import AuthContext from "../../../contexts/AuthContext";

const device_countries = countries.map((country) => {
  return {
    key: country.code,
    label: country.name,
  };
});

const inverterPhaseStatusOptions = [
  {
    key: "UNKNOWN",
    label: "home_settings_field_inverter_on_phase_unknown",
  },
  {
    key: "YES",
    label: "home_settings_field_inverter_on_phase_yes",
  },
  {
    key: "NO",
    label: "home_settings_field_inverter_on_phase_no",
  },
];

const inverterPhaseFieldKeys = [
  "inverter_on_phase_1",
  "inverter_on_phase_2",
  "inverter_on_phase_3",
] as const;

const normalizeInverterPhaseFormData = (data: Record<string, string>) => {
  for (const key of inverterPhaseFieldKeys) {
    if (!data[key]) {
      data[key] = "UNKNOWN";
    }
  }
  return data;
};


const getHomeData = (devices: Device[]) => {

  const deviceOptions = devices.map((device) => {
    return {
      key: device.id.toString(),
      label: device.name
    }
  })

  const masterDeviceOptions = [{key: "", label: '-'}, ...deviceOptions]

  return [
    {
      type: "section",
      title: "home_settings_section_general_data",
    },
    {
      type: "value",
      key: "name",
      data_type: "string",
      label: "home_settings_field_home_name",
    },
    {
      type: "value",
      key: "country",
      data_type: "select",
      label: "home_settings_field_home_country",
      values: device_countries,
    },
    {
      type: "value",
      key: "city",
      data_type: "string",
      label: "home_settings_field_home_city",
    },
    {
      type: "value",
      key: "postal_code",
      data_type: "string",
      label: "home_settings_field_home_postal_code",
    },
    {
      type: "value",
      key: "street",
      data_type: "string",
      label: "home_settings_field_home_street",
    },
    {
      type: "value",
      key: "number",
      data_type: "string",
      label: "home_settings_field_home_number",
    },
    {
      type: "section",
      title: "home_settings_section_connectivity",
    },
    {
      type: "value",
      key: "dongle_ip",
      data_type: "string",
      label: "home_settings_field_dongle_ip",
      tooltip: "home_settings_field_dongle_ip_tooltip",
    },
    {
      type: "value",
      key: "dongle_refresh_frequency",
      data_type: "number",
      label: "home_settings_field_dongle_refresh_frequency",
      tooltip: "home_settings_field_dongle_refresh_frequency_tooltip",
      unit: "s",
    },
    {
      type: "value",
      key: "authorize_external_control",
      data_type: "boolean",
      label: "home_settings_field_authorize_external_control",
      tooltip: "home_settings_field_authorize_external_control_tooltip",
    },
    {
      type: "section",
      title: "home_settings_section_installation",
    },
    {
      type: "value",
      key: "inverter_connection_type",
      data_type: "select",
      label: "home_settings_field_inverter_connection_type",
      tooltip: "home_settings_field_inverter_connection_type_tooltip",
      values: [
        {
          key: "1x230V",
          label: "home_settings_field_inverter_connection_type_1x230v",
        },
        {
          key: "3x230V",
          label: "home_settings_field_inverter_connection_type_3x230v",
        },
        {
          key: "3x400+N",
          label: "home_settings_field_inverter_connection_type_3x400n",
        },
      ],
    },
    {
      type: "value",
      key: "inverter_power",
      data_type: "number",
      label: "home_settings_field_inverter_power",
      tooltip: "home_settings_field_inverter_power_tooltip",
      unit: "W",
    },
    {
      type: "value",
      key: "inverter_on_phase_1",
      data_type: "select",
      label: "home_settings_field_inverter_on_phase_1",
      tooltip: "home_settings_field_inverter_on_phase_tooltip",
      values: inverterPhaseStatusOptions,
    },
    {
      type: "value",
      key: "inverter_on_phase_2",
      data_type: "select",
      label: "home_settings_field_inverter_on_phase_2",
      tooltip: "home_settings_field_inverter_on_phase_tooltip",
      values: inverterPhaseStatusOptions,
    },
    {
      type: "value",
      key: "inverter_on_phase_3",
      data_type: "select",
      label: "home_settings_field_inverter_on_phase_3",
      tooltip: "home_settings_field_inverter_on_phase_tooltip",
      values: inverterPhaseStatusOptions,
    },
    {
      type: "value",
      key: "elec_connection_type",
      data_type: "select",
      label: "home_settings_field_elec_connection_type",
      tooltip: "home_settings_field_elec_connection_type_tooltip",
      values: [
        {
          key: "1x230V",
          label: "home_settings_field_elec_connection_type_1x230v",
        },
        {
          key: "3x230V",
          label: "home_settings_field_elec_connection_type_3x230v",
        },
        {
          key: "3x400+N",
          label: "home_settings_field_elec_connection_type_3x400n",
        },
      ],
    },
    {
      type: "section",
      title: "home_settings_section_capacity_pricing",
    },
    {
      type: "value",
      key: "is_capacity_price_used",
      data_type: "boolean",
      label: "home_settings_field_is_capacity_price_used",
      tooltip: "home_settings_field_is_capacity_price_used_tooltip",
    },
    {
      type: "value",
      key: "capacity_price_power_threshold",
      data_type: "number",
      label: "home_settings_field_capacity_price_power_threshold",
      tooltip: "home_settings_field_capacity_price_power_threshold_tooltip",
      unit: "W",
    },
    {
      type: "value",
      key: "target_power_not_compensated",
      data_type: "number",
      label: "home_settings_field_target_power_not_compensated",
      tooltip: "home_settings_field_target_power_not_compensated_tooltip",
      unit: "W",
    },
    {
      type: "section",
      title: "home_settings_section_energy_prices",
    },
    {
      type: "value",
      key: "saving_per_self_consumed_kwh",
      data_type: "number",
      label: "home_settings_field_saving_per_self_consumed_kwh",
      tooltip: "home_settings_field_saving_per_self_consumed_kwh_tooltip",
      unit: "€/kWh",
      allow_float: true,
      colorIndicator: ["#72A363", "#3b6bb8"],
    },
    {
      type: "value",
      key: "selling_price_exported_kwh",
      data_type: "number",
      label: "home_settings_field_selling_price_exported_kwh",
      tooltip: "home_settings_field_selling_price_exported_kwh_tooltip",
      unit: "€/kWh",
      allow_float: true,
      colorIndicator: ["#D89537"],
    },
    {
      type: "value",
      key: "buying_price_imported_kwh",
      data_type: "number",
      label: "home_settings_field_buying_price_imported_kwh",
      tooltip: "home_settings_field_buying_price_imported_kwh_tooltip",
      unit: "€/kWh",
      allow_float: true,
      colorIndicator: ["#a63d50", "#041a1c"],
    },
    {
      type: "value",
      key: "receive_energy_report_emails",
      data_type: "boolean",
      label: "home_settings_field_receive_energy_report_emails",
      tooltip: "home_settings_field_receive_energy_report_emails_tooltip",
    },
    {
      type: "section",
      title: "home_settings_section_dual",
    },
    {
      type: "value",
      key: "percent_dual",
      data_type: "number",
      label: "home_settings_field_percent_dual",
      tooltip: "home_settings_field_percent_dual_tooltip",
      unit: "%",
    },
    {
      type: "value",
      key: "master_device",
      data_type: "select",
      label: "home_settings_field_master_device",
      tooltip: "home_settings_field_master_device_tooltip",
      values: masterDeviceOptions,
    },
  ];
}


const HomeSettings = ({
  home,
  updateHome,
  deleteHome,
  fetchHome,
  devices,
}: {
  home: Home;
  updateHome: any;
  deleteHome: any;
  fetchHome: (id: number) => void;
  devices: Device[];
}) => {
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [formData, setFormData] = useState<any | null>(null);
  const base_data = useRef<string | null>(null);
  const { t } = useTranslation("settings");
  const { notification } = App.useApp();
  const [errors, setErrors] = useState<string[]>([]);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isBasicUser = !user.is_installer && !user.is_advanced_user;
  const [createHomeOpen, setCreateHomeOpen] = useState(false);
  const [createDeviceOpen, setCreateDeviceOpen] = useState(false);
  const [configureDongleWiFiOpen, setConfigureDongleWiFiOpen] = useState(false);
  const [detectPhasesLoading, setDetectPhasesLoading] = useState(false);

  const homeData = useMemo(() => {
    if (!devices || !home) {
      return []
    }
    return getHomeData(devices.filter((device) => device.home === home.id))
  }, [devices, home])

  const homeDevices = useMemo(
    () => (devices ?? []).filter((device) => device.home === home.id),
    [devices, home],
  );

  const existing_device = useMemo(() => homeDevices.length !== 0, [homeDevices]);

  const applySingleDeviceMasterDefault = (data: Record<string, string>) => {
    if (!data.master_device && homeDevices.length === 1) {
      data.master_device = homeDevices[0].id.toString();
    }
    return data;
  };

  const effectiveMasterDeviceId = useMemo(() => {
    if (formData?.master_device) return formData.master_device;
    if (homeDevices.length === 1) return homeDevices[0].id.toString();
    return "";
  }, [formData?.master_device, homeDevices]);

  useEffect(() => {
    if (homeData && home) {
      const data = normalizeInverterPhaseFormData(
        applySingleDeviceMasterDefault(generate_from_device(homeData, home))
      );
      setFormData(data);
      setErrors([]);
      base_data.current = JSON.stringify(data);
    }
  }, [home, homeData, homeDevices]);

  const cancel = () => {
    const data = normalizeInverterPhaseFormData(
      applySingleDeviceMasterDefault(generate_from_device(homeData, home))
    );
    setFormData(data);
    setErrors([]);
    base_data.current = JSON.stringify(data);
  };

  useEffect(() => {
    setErrors([]);
  }, [formData]);

  const detectInverterPhases = async () => {
    setDetectPhasesLoading(true);
    try {
      const response = await axios
        .post(`/api-v1/home/${home.id}/detect-inverter-phases/`, {})
        .then((e) => e.data);

      if (response?.home) {
        updateHome({
          id: home.id,
          data: response.home,
          updated: true,
        });
      }

      if (response?.error) {
        notification.error({
          message: t("home_settings_detect_inverter_phases_error"),
          description: response.error,
          placement: "topLeft",
        });
      } else if (response?.skipped) {
        notification.warning({
          message: t("home_settings_detect_inverter_phases_skipped"),
          description: response.skip_reason
            ? t(`home_settings_detect_inverter_phases_skip_${response.skip_reason}`, {
                defaultValue: response.skip_reason,
              })
            : undefined,
          placement: "topLeft",
        });
      } else if (response?.updated) {
        notification.success({
          message: t("home_settings_detect_inverter_phases_success"),
          placement: "topLeft",
        });
      } else {
        notification.info({
          message: t("home_settings_detect_inverter_phases_unchanged"),
          placement: "topLeft",
        });
      }
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        e?.message;
      notification.error({
        message: t("home_settings_detect_inverter_phases_error"),
        description: typeof detail === "string" ? detail : undefined,
        placement: "topLeft",
      });
    } finally {
      setDetectPhasesLoading(false);
    }
  };

  const save = async () => {
    setLoading(true);
    let error: any | null = null;
    const response = await axios
      .patch(
        `/api-v1/home/${home.id}/`,
        generate_for_device(homeData, formData)
      )
      .then((e) => e.data)
      .catch((e) => {
        error = e.response.data;
        return null;
      });

    if (response) {
      updateHome({
        id: home.id,
        data: response,
        updated: true,
      });
      notification.success({
        message: t("messages:settings_update_success_message"),
        placement: "topLeft",
      });
      setErrors([]);
    } else {
      if (error.length == undefined) {
        const error_lst = [];
        for (const [key, value] of Object.entries(error)) {
          error_lst.push(key);
          const updated: any = value;
          const error_message = homeData.find((d) => d.key == key)?.label;
          if (updated.includes("This field may not be blank.")) {
            notification.error({
              message: t("messages:field_update_error", {
                field_name: t(error_message ? error_message : ""),
              }),
              description: t("messages:field_cannot_be_empty"),
              placement: "topLeft",
            });
          } else if (
            updated.includes("Ensure this value is greater than or equal to 5.")
          ) {
            notification.error({
              message: t("messages:field_update_error", {
                field_name: t(error_message ? error_message : ""),
              }),
              description: t("messages:field_more_than_x", { value: 5 }),
              placement: "topLeft",
            });
          } else if (
            updated.includes("Ensure this value is greater than or equal to 0.")
          ) {
            notification.error({
              message: t("messages:field_update_error", {
                field_name: t(error_message ? error_message : ""),
              }),
              description: t("messages:field_more_than_x", { value: 0 }),
              placement: "topLeft",
            });
          } else if (
            updated.includes("Ensure this value is less than or equal to 60")
          ) {
            notification.error({
              message: t("messages:field_update_error", {
                field_name: t(error_message ? error_message : ""),
              }),
              description: t("messages:field_between_1_60"),
              placement: "topLeft",
            });
          } else if (key == "non_field_errors") {
            const desc = Array.isArray(value) ? value[0] : value;
            notification.error({
              message: t("messages:settings_update_error_message"),
              description: desc,
              placement: "topLeft",
            });
          } else {
            notification.error({
              message: t("messages:field_update_error", {
                field_name: t(error_message ? error_message : ""),
              }),
              description: t("messages:field_error"),
              placement: "topLeft",
            });
          }
        }
        setErrors(error_lst);
      } else {
        notification.error({
          message: t("messages:settings_update_error_message"),
          description: error[0],
          placement: "topLeft",
        });
      }
    }
    setLoading(false);
  };

  const delete_home = () => {
    setDeleteLoading(true);
    const home_name = home.name;
    deleteHome(home.id).then(() => {
      setDeleteLoading(false);
      setDeleteOpen(false);
      notification.error({
        message: t("messages:home_deleted_message", {
          home_name: home_name,
        }),
        placement: "topLeft",
      });
      navigate('/')
    });
  };

  if (!formData) return null;

  return (
    <div className="device-home-settings">
      <CreateHomeModal
        open={createHomeOpen}
        close={() => setCreateHomeOpen(false)}
      />
      <CreateDeviceModal
        open={createDeviceOpen}
        device={home.id}
        close={() => setCreateDeviceOpen(false)}
      />
      <ConfigureWiFiModal
        open={configureDongleWiFiOpen}
        close={() => setConfigureDongleWiFiOpen(false)}
        titleKey="configure_dongle_wifi_modal_title"
        descriptionKey="configure_dongle_wifi_modal_description"
        descriptionOptions={{ homeName: home.name ? ` (${home.name})` : "" }}
        wifiSsid="SolisEco"
      />
      <div className="save-header">
        {isBasicUser && (
          <div className="secondary-actions-group">
            <button
              type="button"
              className="inline-action-btn"
              onClick={() => setCreateHomeOpen(true)}
            >
              <span className="icon-wrapper">
                <NewHome />
              </span>
              <span>{t("home:new_home_modal_title")}</span>
            </button>
            <button
              type="button"
              className="inline-action-btn"
              onClick={() => setCreateDeviceOpen(true)}
            >
              <span className="icon-wrapper">
                <NewDevice />
              </span>
              <span>{t("home:new_device_modal_title")}</span>
            </button>
          </div>
        )}
        <div className="main-btn-wrapper">
          <MainBtn
            loading={loading}
            disabled={JSON.stringify(formData) === base_data.current}
            title={t("save_updates")}
            onClick={save}
          />
          <MainBtn
            title={t("main:cancel")}
            color="secondary"
            onClick={cancel}
          />
        </div>
      </div>
      <div className="device-home-container">
        <div className="settings-title">
          <span>{t("settings_of_the_home", { name: home.name })}</span>
        </div>
        { homeData.map((data: any, index: number) => {
          if (data.type === "warning") {
            return (
              <div key={ index } className="section-warning">
                <WarningOutlined style={{fontSize: 18, marginRight: "0.5rem"}}/>
                <span>{t(data.message)}</span>
              </div>
            )
          }
          if (data.type === "section") {
            return (
              <div key={ index } className="section-title">
                <span>{t(data.title)}</span>
              </div>
            );
          }
          if (data.data_type === "string") {
            const isDongleIp = data.key === "dongle_ip";
            if (isDongleIp) {
              return (
                <Fragment key={index}>
                  <div className="setting-block">
                    <span>
                      {t(data.label)}
                      {data.tooltip && (
                        <Tooltip title={t(data.tooltip)}>
                          <InfoCircleOutlined
                            style={{
                              color: "#012124",
                              opacity: 0.4,
                              marginLeft: "5px",
                            }}
                          />
                        </Tooltip>
                      )}
                    </span>
                    <div className="input-container">
                      <Input
                        value={formData[data.key]}
                        status={errors.includes(data.key) ? "error" : ""}
                        onChange={(e) =>
                          setFormData({ ...formData, [data.key]: e.target.value })
                        }
                      />
                      {data.unit && <span className="unit">{data.unit}</span>}
                    </div>
                  </div>
                  <div className="setting-block">
                    <span>
                      {t(
                        "home_settings_dongle_autodetect_section",
                        "Check dongle status",
                      )}
                    </span>
                    <div className="input-container">
                      <DongleIpDetectToolbar
                        masterDeviceId={effectiveMasterDeviceId}
                        onDetectCompleted={() => fetchHome(home.id)}
                      />
                    </div>
                  </div>
                </Fragment>
              );
            }
            return (
              <div key={index} className="setting-block">
                <span>
                  { t(data.label) }
                  {data.tooltip && (
                    <Tooltip title={t(data.tooltip)}>
                      <InfoCircleOutlined
                        style={{
                          color: "#012124",
                          opacity: 0.4,
                          marginLeft: "5px",
                        }}
                      />
                    </Tooltip>
                  )}
                </span>
                <div className="input-container">
                  <Input
                    value={formData[data.key]}
                    status={errors.includes(data.key) ? "error" : ""}
                    onChange={(e) =>
                      setFormData({ ...formData, [data.key]: e.target.value })
                    }
                  />
                  {data.unit && <span className="unit">{data.unit}</span>}
                </div>
              </div>
            );
          } else if (data.data_type === "number") {
            return (
              <div key={index} className="setting-block">
                <span>
                  { t(data.label) }
                  {data.colorIndicator && (
                    <span className="energy-price-color-indicator" style={{ marginLeft: 8 }}>
                      {(Array.isArray(data.colorIndicator) ? data.colorIndicator : [data.colorIndicator]).map(
                        (color: string, i: number) => (
                          <span
                            key={i}
                            style={{
                              display: "inline-block",
                              width: 12,
                              height: 12,
                              backgroundColor: color,
                              borderRadius: 2,
                              marginLeft: i > 0 ? 2 : 0,
                              verticalAlign: "middle",
                            }}
                            title={t(data.tooltip || "")}
                          />
                        )
                      )}
                    </span>
                  )}
                  {data.tooltip && (
                    <Tooltip title={t(data.tooltip)}>
                      <InfoCircleOutlined
                        style={{
                          color: "#012124",
                          opacity: 0.4,
                          marginLeft: "5px",
                        }}
                      />
                    </Tooltip>
                  )}
                </span>
                <div className="input-container">
                  <Input
                    value={formData[data.key]}
                    status={errors.includes(data.key) ? "error" : ""}
                    onChange={ (e) => {
                      setFormData({ ...formData, [data.key]: e.target.value })
                    }
                    }
                    type="number"
                  />
                  {data.unit !== null && (
                    <span className="unit">{data?.unit}</span>
                  )}
                </div>
              </div>
            );
          } else if (data.data_type === "select" && data.values) {
            const isLastInverterPhaseField = data.key === "inverter_on_phase_3";
            return (
              <Fragment key={index}>
                <div className="setting-block">
                  <span>
                    { t(data.label) }
                    {data.tooltip && (
                      <Tooltip title={t(data.tooltip)}>
                        <InfoCircleOutlined
                          style={{
                            color: "#012124",
                            opacity: 0.4,
                            marginLeft: "5px",
                          }}
                        />
                      </Tooltip>
                    )}
                  </span>
                  <div className="input-container">
                    <Select
                      value={formData[data.key]}
                      onChange={(e) =>
                        setFormData({ ...formData, [data.key]: e })
                      }
                    >
                      {data.values.map((value: any, optionIndex: number) => (
                        <Select.Option value={value.key} key={optionIndex}>
                          {t(value.label)}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </div>
                {isLastInverterPhaseField && user.is_installer && (
                  <div className="setting-block">
                    <span>
                      {t("home_settings_detect_inverter_phases_label")}
                      <Tooltip title={t("home_settings_detect_inverter_phases_tooltip")}>
                        <InfoCircleOutlined
                          style={{
                            color: "#012124",
                            opacity: 0.4,
                            marginLeft: "5px",
                          }}
                        />
                      </Tooltip>
                    </span>
                    <div className="input-container">
                      <Button
                        size="small"
                        loading={detectPhasesLoading}
                        disabled={detectPhasesLoading || !existing_device}
                        onClick={detectInverterPhases}
                      >
                        {t("home_settings_detect_inverter_phases_button")}
                      </Button>
                    </div>
                  </div>
                )}
              </Fragment>
            );
          } else if (data.data_type === "boolean") {
            return (
              <div key={index} className="setting-block">
                <span>
                  { t(data.label) }
                  {data.tooltip && (
                    <Tooltip title={t(data.tooltip)}>
                      <InfoCircleOutlined
                        style={{
                          color: "#012124",
                          opacity: 0.4,
                          marginLeft: "5px",
                        }}
                      />
                    </Tooltip>
                  )}
                </span>
                <div className="input-container">
                  <Switch
                    value={formData[data.key] === "true"}
                    onChange={(checked) =>
                      setFormData({
                        ...formData,
                        [data.key]: checked ? "true" : "false",
                      })
                    }
                  />
                </div>
              </div>
            );
          }
        })}
        <div className="delete-container">
          <Button
            className="wifi-config-button"
            onClick={() => setConfigureDongleWiFiOpen(true)}
            style={{ marginRight: '8px' }}
          >
            {t("home_settings_setup_dongle_wifi_button", "Setup Dongle WiFi")}
          </Button>
          {existing_device ? (
            <Popover
              content={t("home_settings_cannot_delete_home_description")}
              placement="topRight"
              title={t("home_settings_cannot_delete_home")}
            >
              <Button disabled={true} danger>
                {t("home_settings_delete_button")}
              </Button>
            </Popover>
          ) : (
            <Popconfirm
              placement="topRight"
              title={t("home_settings_delete_home")}
              description={t("home_settings_delete_home_description")}
              open={deleteOpen}
              onConfirm={delete_home}
              onCancel={() => setDeleteOpen(false)}
              okButtonProps={{ loading: deleteLoading }}
              icon={<QuestionCircleOutlined style={{ color: "red" }} />}
            >
              <Button onClick={() => setDeleteOpen(true)} danger>
                {t("home_settings_delete_button")}
              </Button>
            </Popconfirm>
          )}
        </div>
      </div>
    </div>
  );
};

export default connect<StateProps, DispatchProps>(
  null,
  mapDispatchToProps
)(HomeSettings);
